import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const BondCalculator = () => {
  const [principal, setPrincipal] = useState(1500000);
  const [rate, setRate] = useState(11.75);
  const [term, setTerm] = useState(20);
  const [monthlyRepayment, setMonthlyRepayment] = useState(0);

  const calculateBond = () => {
    const P = parseFloat(principal);
    const R_annual = parseFloat(rate);
    const N = parseFloat(term) * 12;

    if (P <= 0 || R_annual <= 0 || N <= 0) {
      setMonthlyRepayment(0);
      return;
    }

    const i = (R_annual / 100) / 12;
    const power_factor = Math.pow(1 + i, N);
    const M = P * (i * power_factor) / (power_factor - 1);

    if (isNaN(M) || !isFinite(M)) {
      setMonthlyRepayment(0);
    } else {
      setMonthlyRepayment(M);
    }
  };

  useEffect(() => {
    calculateBond();
  }, [principal, rate, term]);

  const formatZAR = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <Card className="max-w-lg mx-auto">
        <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50">
          <CardTitle className="text-2xl flex items-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            <i className="fas fa-calculator text-yellow-600 mr-2"></i>
            Bond Calculator (ZAR)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={(e) => { e.preventDefault(); calculateBond(); }} className="space-y-4">
            <div>
              <Label htmlFor="principal">Principal Amount (R)</Label>
              <Input
                id="principal"
                type="number"
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                placeholder="1500000"
                data-testid="bond-principal-input"
              />
            </div>

            <div>
              <Label htmlFor="rate">Annual Interest Rate (%)</Label>
              <Input
                id="rate"
                type="number"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="11.75"
                data-testid="bond-rate-input"
              />
            </div>

            <div>
              <Label htmlFor="term">Term in Years</Label>
              <Input
                id="term"
                type="number"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="20"
                data-testid="bond-term-input"
              />
            </div>

            <Button type="submit" className="w-full bg-yellow-600 hover:bg-yellow-700" data-testid="calculate-bond-btn">
              Calculate
            </Button>
          </form>

          <div className="mt-6 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg text-center border-2 border-emerald-200">
            <p className="text-sm text-gray-600 mb-2">Monthly Repayment</p>
            <div className="text-4xl font-extrabold text-emerald-700" data-testid="bond-repayment-display">
              {monthlyRepayment > 0 ? formatZAR(monthlyRepayment) : 'R0'}
            </div>
            
            {monthlyRepayment > 0 && (
              <div className="mt-4 pt-4 border-t border-emerald-200 text-sm text-gray-600 space-y-1">
                <p>Total payments: {term * 12} months</p>
                <p>Total amount: {formatZAR(monthlyRepayment * term * 12)}</p>
                <p>Total interest: {formatZAR((monthlyRepayment * term * 12) - principal)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </main>
  );
};

export default BondCalculator;